import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { safeJsonLd } from "./utils/safeJsonLd";
import {
  ArrowRight, Menu, X, Heart, Users, Stethoscope,
  Apple, Shield, ChevronDown, Sparkles, Target,
  MessageCircle, Activity, Facebook, Instagram, Youtube,
  Baby, Leaf, Clock, Globe, Dumbbell, FlaskConical, Star, ChevronLeft, ChevronRight
} from "lucide-react";

const Anim = ({ children, className = "", delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}>{children}</motion.div>
  );
};

const WHATSAPP_PHONE = "919108818369";

const SectionHeading = ({ eyebrow, heading, children, light = false }) => (
  <Anim>
    <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
      {eyebrow && <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">{eyebrow}</span>}
      <h2 className={`font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mt-3 ${light ? "text-bone" : "text-emerald_deep"}`}>{heading}</h2>
      {children && <div className={`mt-4 text-base leading-relaxed max-w-2xl mx-auto ${light ? "text-bone/70" : "text-text_muted"}`}>{children}</div>}
    </div>
  </Anim>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-surface border border-border_custom p-6 md:p-8 rounded-xl hover:shadow-md transition-shadow h-full">
    {Icon && <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4"><Icon size={20} className="text-accent" /></div>}
    <h3 className="font-serif text-lg md:text-xl text-emerald_deep mb-3">{title}</h3>
    <div className="text-sm text-text_muted leading-relaxed">{children}</div>
  </div>
);

const FAQItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-border_custom">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left group" aria-expanded={open}>
      <span className="font-serif text-base md:text-lg text-emerald_deep pr-4 group-hover:text-accent transition-colors">{q}</span>
      <ChevronDown size={18} className={`text-text_muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-accent" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="text-sm text-text_muted leading-relaxed pb-5">{a}</p>
    </motion.div>
  </div>
);

const LandingHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const navItems = [
    { label: "Home", href: "/" },
    { label: "Fertility Nutrition", href: "/fertility-nutrition" },
    { label: "Female Fertility", href: "/female-fertility-nutrition" },
    { label: "Male Fertility", href: "/male-fertility-nutrition" },
    { label: "Contact", href: "/#contact" }
  ];
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-bone/95 backdrop-blur-xl shadow-sm" : "bg-transparent"}`} data-testid="cf-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-serif text-2xl tracking-tight">
            <span className={`font-semibold ${scrolled ? "text-emerald_deep" : "text-bone"}`}>Fitly</span>
            <span className={`font-light ml-1 ${scrolled ? "text-text_muted" : "text-bone/60"}`}>India</span>
          </a>
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className={`hover:text-accent transition-colors text-sm tracking-wide ${scrolled ? "text-text_muted" : "text-bone/70"}`}>{item.label}</a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="/consultation" className="bg-accent text-emerald_deep px-7 py-2.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-2 rounded-full">
              Book Consultation <ArrowRight size={14} />
            </a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${scrolled ? "text-emerald_deep" : "text-bone"}`}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden py-6 bg-surface border border-border_custom rounded-xl mt-2 mb-4 shadow-lg">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className="block w-full text-left px-6 py-3 text-text_muted hover:text-emerald_deep hover:bg-bone/50 text-sm tracking-wide">{item.label}</a>
            ))}
            <div className="px-6 pt-3">
              <a href="/consultation" className="block w-full bg-emerald_deep text-bone py-3 text-sm tracking-wide text-center rounded-full">Book Consultation</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const LandingFooter = () => (
  <footer className="bg-emerald_deep text-bone py-16 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl mb-4"><span className="text-accent">Fitly</span> India</div>
          <p className="text-bone/60 leading-relaxed max-w-sm">Fertility Nutrition & Conception Care for women, men and couples across India and internationally.</p>
          <div className="flex gap-4 mt-6">
            {[[Facebook, "https://www.facebook.com/294056110466070"], [Instagram, "https://www.instagram.com/fitly_india"], [Youtube, "https://www.youtube.com/fitly_india"]].map(([Icon, href]) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center hover:bg-accent/20 transition-colors"><Icon size={16} className="text-bone/70" /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {[["Home", "/"], ["Fertility Nutrition", "/fertility-nutrition"], ["Female Fertility", "/female-fertility-nutrition"], ["Male Fertility", "/male-fertility-nutrition"], ["Couple Fertility", "/couple-fertility-nutrition"], ["Consultation", "/consultation"]].map(([label, href]) => (
              <li key={label}><a href={href} className="text-bone/60 hover:text-accent transition-colors text-sm">{label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-bone/60">
            <li><a href="tel:+919108818369" className="hover:text-accent transition-colors">+91 91088 18369</a></li>
            <li><a href="mailto:fitlyindiahealth@gmail.com" className="hover:text-accent transition-colors">fitlyindiahealth@gmail.com</a></li>
            <li>Mon - Sat, 10 AM - 6 PM</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/10 pt-8 text-center text-bone/40 text-xs">&copy; {new Date().getFullYear()} Fitly India. All rights reserved.</div>
    </div>
  </footer>
);

const faqData = [
  { q: "Why should we join as a couple?", a: "Conception depends on both partners' health. Male factors contribute to approximately 40-50% of fertility challenges. A couple program ensures both partners receive tailored nutrition — her egg quality, cycle health and hormonal balance alongside his sperm health, DNA integrity and metabolic wellness." },
  { q: "How does the couple program work?", a: "Both partners receive individual consultations and personalised nutrition plans. Plans reflect each person's dietary preferences, health history, reports and specific goals. Regular follow-ups track both partners' progress together." },
  { q: "Can we join if only one partner has a diagnosed issue?", a: "Absolutely. Even when a specific diagnosis exists for one partner, supporting both partners' overall reproductive health creates a stronger foundation for conception. The non-diagnosed partner's nutrition is optimised for general preconception wellness." },
  { q: "Is it more cost-effective than individual plans?", a: "Yes. Couple programs at Fitly are priced lower than two individual programs combined, making it both clinically and financially sensible to work together." },
  { q: "What if we have different dietary preferences?", a: "Each partner's plan is fully individualised. Vegetarian, non-vegetarian, regional cuisine preferences and food intolerances are all accommodated separately within the couple program." },
  { q: "How long does the program last?", a: "Programs are typically 90 days, aligned with the egg maturation and sperm development cycles. Extensions and ongoing support are available based on your journey and progress." },
  { q: "Are consultations done together or separately?", a: "The initial assessment can include both partners. Follow-up consultations may be joint or individual depending on what works best. Both options are available." },
  { q: "Do you work with our fertility specialist?", a: "Yes. Fitly encourages appropriate medical evaluation and works alongside your treating specialist. Nutrition complements — never replaces — medical care." },
  { q: "Can we join from outside India?", a: "Yes. All consultations are conducted online via video or voice call. Couples from anywhere in the world can access Fitly's couple fertility nutrition programs." },
  { q: "What results can we expect?", a: "No food or supplement can guarantee conception. However, optimised nutrition supports hormonal balance, egg and sperm quality, metabolic health and overall reproductive wellness — all of which improve the conditions for natural or assisted conception." }
];

const structuredData = {
  faq: { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  breadcrumb: { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fitlyindia.org/" },
    { "@type": "ListItem", "position": 2, "name": "Fertility Nutrition", "item": "https://fitlyindia.org/fertility-nutrition/" },
    { "@type": "ListItem", "position": 3, "name": "Couple Fertility Nutrition", "item": "https://fitlyindia.org/couple-fertility-nutrition/" }
  ]},
  service: { "@context": "https://schema.org", "@type": "Service", "name": "Couple Fertility Nutrition Program", "provider": { "@type": "Organization", "name": "Fitly India", "url": "https://fitlyindia.org", "telephone": "+919108818369" }, "serviceType": "Couple Fertility Nutrition", "areaServed": { "@type": "Country", "name": "India" }, "description": "Personalised couple fertility nutrition for both partners — her egg quality, PCOS management and hormonal balance alongside his sperm health, lifestyle and metabolic wellness." }
};

const coupleTestimonials = [
  { name: "Priya & Karthik M.", location: "Bangalore", condition: "Fertility Support", quote: "After 3 years of trying, we finally conceived naturally within 4 months of following Fitly's fertility nutrition program. The customized diet and guidance changed everything for us!" },
  { name: "Anjali & Rahul S.", location: "Hyderabad", condition: "Low AMH & Male Factor", quote: "We were told IVF was our only option. Fitly's couple program helped us both — my AMH improved and my husband's sperm count doubled. We're now expecting twins naturally!" },
  { name: "Fathima & Irfan Z.", location: "Dubai", condition: "Unexplained Infertility", quote: "We joined the online Elite program from Dubai after 4 years of unexplained infertility. Nadiya's personalized approach found gaps our doctors missed. Conceived in month 5!" },
  { name: "Swathi & Arun D.", location: "Bangalore", condition: "Pregnancy Nutrition", quote: "Joined Premium Plus for our entire pregnancy journey. The trimester-wise nutrition plans gave us so much confidence. Our little one is healthy and thriving!" },
];

const CoupleTestimonialCarousel = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const len = coupleTestimonials.length;

  const next = useCallback(() => { setDir(1); setIdx((i) => (i + 1) % len); }, [len]);
  const prev = useCallback(() => { setDir(-1); setIdx((i) => (i - 1 + len) % len); }, [len]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const t = coupleTestimonials[idx];

  return (
    <section className="py-20 md:py-28 bg-emerald_deep relative overflow-hidden" data-testid="cf-testimonials">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #D4AF37 1px, transparent 1px)", backgroundSize: "45px 45px" }}></div>
      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
        <Anim>
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">What Couples Say</span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-bone mt-3">
              Real Couples, <span className="italic font-light">Real Results</span>
            </h2>
          </div>
        </Anim>

        <div className="relative min-h-[240px] md:min-h-[200px]">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={t.name}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="text-center"
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <blockquote
                className="font-serif text-xl md:text-2xl text-bone/90 leading-relaxed italic max-w-3xl mx-auto mb-8"
                data-testid={`cf-testimonial-quote-${idx}`}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <span className="text-accent font-serif text-sm">{t.name.charAt(0)}</span>
                </div>
                <div className="text-left">
                  <p className="text-bone text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-bone/50">{t.location} — {t.condition}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mt-10">
          <button onClick={prev} data-testid="cf-testimonial-prev" className="w-10 h-10 rounded-full border border-bone/20 flex items-center justify-center text-bone/60 hover:text-accent hover:border-accent transition-colors" aria-label="Previous testimonial">
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {coupleTestimonials.map((ct, i) => (
              <button
                key={ct.name}
                onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                data-testid={`cf-testimonial-dot-${i}`}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${i === idx ? "bg-accent w-6" : "bg-bone/30 hover:bg-bone/50"}`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
          <button onClick={next} data-testid="cf-testimonial-next" className="w-10 h-10 rounded-full border border-bone/20 flex items-center justify-center text-bone/60 hover:text-accent hover:border-accent transition-colors" aria-label="Next testimonial">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default function CoupleFertilityNutritionPage() {
  const [openFaq, setOpenFaq] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-bone text-text_main" data-testid="couple-fertility-page">
      <Helmet>
        <title>Couple Fertility Nutrition in India | For Her & Him Together | Fitly India</title>
        <meta name="description" content="Personalised couple fertility nutrition for both partners. Her egg quality, PCOS and hormonal wellness alongside his sperm health and lifestyle optimisation. Online fertility nutritionist in India from Fitly India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://fitlyindia.org/couple-fertility-nutrition/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitlyindia.org/couple-fertility-nutrition/" />
        <meta property="og:title" content="Couple Fertility Nutrition in India | Fitly India" />
        <meta property="og:description" content="Personalised couple fertility nutrition for both partners — her egg quality, PCOS and hormonal wellness alongside his sperm health and lifestyle optimisation." />
        <meta property="og:site_name" content="Fitly India" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.faq) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.service) }} />

      <LandingHeader />

      <main>
        {/* HERO */}
        <section className="relative min-h-screen flex items-center bg-emerald_deep pt-20 overflow-hidden" data-testid="cf-hero">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 70% 70%, #D4AF37 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10 py-16 md:py-24">
            <div className="max-w-3xl">
              <Anim>
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex items-center gap-2 text-xs text-bone/40">
                    <li><a href="/" className="hover:text-accent transition-colors">Home</a></li><li>/</li>
                    <li><a href="/fertility-nutrition" className="hover:text-accent transition-colors">Fertility Nutrition</a></li><li>/</li>
                    <li className="text-accent">Couple Fertility</li>
                  </ol>
                </nav>
              </Anim>
              <Anim delay={0.1}>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mb-6">
                  Couple Fertility Nutrition<br /><span className="italic font-light text-accent">Together, Stronger</span>
                </h1>
              </Anim>
              <Anim delay={0.2}>
                <p className="text-lg md:text-xl text-bone/80 leading-relaxed mb-6 max-w-2xl">
                  Personalised nutrition for both partners — because conception is a shared journey that depends on both her health and his.
                </p>
              </Anim>
              <Anim delay={0.3}>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  Male factors contribute to approximately 40–50% of fertility challenges. Fitly&apos;s couple programs address both partners simultaneously with individualised plans — her egg quality, cycle health, PCOS management and hormonal balance alongside his sperm health, DNA integrity and metabolic wellness.
                </p>
              </Anim>
              <Anim delay={0.4}>
                <div className="flex flex-wrap gap-3 mb-10">
                  {["Her Egg Quality", "His Sperm Health", "PCOS Support", "IVF Preparation", "Preconception Wellness", "90-Day Programs"].map(tag => (
                    <span key={tag} className="border border-accent/30 text-accent text-xs tracking-wider px-4 py-2 rounded-full">{tag}</span>
                  ))}
                </div>
              </Anim>
              <Anim delay={0.5}>
                <a href="/consultation" data-testid="cf-hero-cta" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book a Couple Consultation <ArrowRight size={16} />
                </a>
              </Anim>
            </div>
          </div>
        </section>

        {/* WHY COUPLES */}
        <section className="py-20 md:py-28 bg-bone" data-testid="cf-why-couples">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Why Together?" heading="Conception Depends on Both Partners">
              <p>Research consistently shows that both partners&apos; nutritional status, lifestyle and metabolic health influence fertility outcomes. Working together is the most effective approach.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { stat: "40–50%", label: "of fertility challenges involve male factors — either as the primary cause or a contributing factor" },
                { stat: "90 Days", label: "is the development window for both eggs and sperm, making nutrition timing critical for both partners" },
                { stat: "2x", label: "the impact when both partners optimise their nutrition simultaneously versus one partner alone" }
              ].map((item, idx) => (
                <Anim key={item.stat} delay={idx * 0.1}>
                  <div className="text-center p-8 bg-surface border border-border_custom rounded-xl">
                    <p className="font-serif text-4xl md:text-5xl text-accent italic tracking-tight mb-3">{item.stat}</p>
                    <p className="text-sm text-text_muted leading-relaxed">{item.label}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* HER & HIS NUTRITION */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="cf-her-his">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 60% 30%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="Personalised for Both" heading="Her Nutrition & His Nutrition" light>
              <p>Each partner receives a fully individualised plan. Here&apos;s what we focus on for each.</p>
            </SectionHeading>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Her */}
              <Anim>
                <div className="bg-bone/5 border border-bone/10 p-8 md:p-10 rounded-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <Heart size={22} className="text-accent" />
                    <h3 className="font-serif text-2xl text-bone italic tracking-tight">For Her</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "Egg quality support during the 90-day maturation window",
                      "PCOS management — insulin sensitivity, cycle regularity",
                      "Hormonal balance and ovulation health",
                      "Antioxidant-rich diet for reproductive protection",
                      "Micronutrient adequacy — folate, iron, vitamin D, zinc",
                      "IVF preparation nutrition when applicable",
                      "Endometriosis and thyroid nutritional support"
                    ].map(item => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-bone/70 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0 mt-2"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a href="/female-fertility-nutrition" className="inline-flex items-center gap-1.5 text-accent text-sm font-medium mt-6 hover:underline">
                    Learn more about female fertility nutrition <ArrowRight size={14} />
                  </a>
                </div>
              </Anim>

              {/* His */}
              <Anim delay={0.15}>
                <div className="bg-bone/5 border border-bone/10 p-8 md:p-10 rounded-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield size={22} className="text-accent" />
                    <h3 className="font-serif text-2xl text-bone italic tracking-tight">For Him</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "Sperm health — count, motility, morphology and DNA integrity",
                      "Antioxidant protection against sperm DNA damage",
                      "Zinc, selenium and omega-3 for reproductive function",
                      "Weight management and metabolic health",
                      "Lifestyle factors — sleep, stress, exercise, habits",
                      "Testosterone support through nutrition",
                      "CoQ10 and mitochondrial energy for sperm motility"
                    ].map(item => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-bone/70 leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0 mt-2"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a href="/male-fertility-nutrition" className="inline-flex items-center gap-1.5 text-accent text-sm font-medium mt-6 hover:underline">
                    Learn more about male fertility nutrition <ArrowRight size={14} />
                  </a>
                </div>
              </Anim>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 md:py-28 bg-bone" data-testid="cf-how">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Our Process" heading="How the Couple Program Works" />
            {[
              { num: "01", title: "Joint Assessment", text: "An in-depth consultation with both partners — understanding each person's health history, dietary patterns, lifestyle, reports and reproductive goals." },
              { num: "02", title: "Individual Analysis", text: "Review of each partner's hormonal reports, nutritional markers, metabolic health and specific areas where nutrition can make a meaningful difference." },
              { num: "03", title: "Two Personalised Plans", text: "Each partner receives their own tailored nutrition plan — her plan addresses female-specific factors while his plan focuses on male reproductive health." },
              { num: "04", title: "Shared Meals, Individual Needs", text: "We design plans that work in a shared kitchen. Common meals with individual additions ensure both partners can eat together without separate cooking." },
              { num: "05", title: "Ongoing Support Together", text: "Regular follow-ups, plan adjustments and accountability for both partners throughout the 90-day program. Progress tracked individually and as a couple." }
            ].map((step, idx) => (
              <Anim key={step.num} delay={idx * 0.1}>
                <div className="flex gap-6 md:gap-10 py-8 border-b border-border_custom group">
                  <span className="font-serif text-4xl md:text-5xl text-accent/20 group-hover:text-accent transition-colors flex-shrink-0">{step.num}</span>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-emerald_deep mb-2 group-hover:text-accent transition-colors">{step.title}</h3>
                    <p className="text-sm text-text_muted leading-relaxed max-w-xl">{step.text}</p>
                  </div>
                </div>
              </Anim>
            ))}
          </div>
        </section>

        {/* COUPLE ADVANTAGES */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="cf-advantages">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="The Couple Advantage" heading="Why Couples See Better Results" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Users, title: "Both Factors Addressed", text: "Instead of optimising only one partner, both reproductive systems are supported simultaneously — covering the full fertility equation." },
                { icon: Target, title: "Shared Accountability", text: "Partners who work together on nutrition changes are more likely to sustain healthy habits. Mutual motivation and support make a real difference." },
                { icon: Apple, title: "Practical Meal Planning", text: "Plans designed for a shared kitchen. Common base meals with individual additions mean less cooking complexity and more real-world compliance." },
                { icon: Clock, title: "Aligned Timing", text: "Both partners' 90-day preparation windows run in parallel, ensuring both are nutritionally optimised at the same time for conception attempts." },
                { icon: FlaskConical, title: "IVF Couple Prep", text: "For couples undergoing IVF, her egg quality nutrition and his sperm health nutrition work in tandem to support the best possible treatment foundation." },
                { icon: Globe, title: "Online & Accessible", text: "Video or voice consultations available for couples across India and internationally. Flexible scheduling for busy partners." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <Card icon={card.icon} title={card.title}>{card.text}</Card>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* COUPLE TESTIMONIALS */}
        <CoupleTestimonialCarousel />

        {/* WHY FITLY */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="cf-why-fitly">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 40% 60%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="The Fitly Difference" heading="Why Choose Fitly for Couple Fertility Nutrition?" light />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Baby, title: "Fertility-Specialised", text: "Dedicated fertility nutrition programs — not generic health coaching. Every plan is built around reproductive health outcomes." },
                { icon: Stethoscope, title: "Expert Team", text: "Mrs. Nadiya (Deep Health Coach, Precision Nutrition L2), Mrs. Revathy R.P (Clinical Nutritionist, Certified in Fertility Nutrition), alongside gynaecologists and specialists." },
                { icon: Sparkles, title: "Evidence-Based", text: "Recommendations grounded in current nutritional science and fertility research. No fad diets, no miracle supplements — practical, proven approaches." },
                { icon: Apple, title: "Indian Food. Real Life.", text: "Familiar foods — dal, roti, rice, sabzi, curd — not imported superfoods. Sustainable nutrition that fits both partners' cultures and routines." },
                { icon: Activity, title: "500+ Couples Helped", text: "Experience across diverse fertility challenges including PCOS, low AMH, male factor, unexplained infertility and IVF preparation." },
                { icon: Dumbbell, title: "Holistic Approach", text: "Beyond food — sleep, stress, exercise, lifestyle and emotional wellness are all part of the couple's fertility optimisation plan." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <div className="bg-bone/5 border border-bone/10 p-6 md:p-8 rounded-xl hover:bg-bone/10 transition-colors h-full">
                    <card.icon size={22} className="text-accent mb-4" />
                    <h3 className="font-serif text-lg text-bone mb-3">{card.title}</h3>
                    <p className="text-sm text-bone/65 leading-relaxed">{card.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="py-20 md:py-28 bg-bone" data-testid="cf-trust">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <Anim>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Medical Responsibility</span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-emerald_deep mt-3 mb-8">
                Nutrition Supports — It Doesn&apos;t <span className="italic font-light">Replace</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <div className="space-y-5 text-base text-text_muted leading-relaxed text-left">
                <p>Infertility can have many causes — some require medical intervention, specialised treatment or assisted reproduction. No food, supplement or diet can guarantee conception.</p>
                <p>Fitly&apos;s couple programs optimise the nutritional and lifestyle factors within both partners&apos; control while encouraging and complementing appropriate medical care from your treating specialist.</p>
                <p>Both partners are encouraged to undergo medical evaluation alongside nutritional optimisation for the most comprehensive approach to their fertility journey.</p>
              </div>
            </Anim>
            <Anim delay={0.2}><div className="gold-divider w-32 mx-auto mt-10"></div></Anim>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="cf-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Common Questions" heading="Couple Fertility Nutrition FAQ" />
            <div className="bg-surface border border-border_custom rounded-xl p-6 md:p-8">
              {faqData.map((faq, idx) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} open={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="cf-final-cta">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <Anim><h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-bone mb-6">Your Journey.<br /><span className="italic font-light text-accent">Together.</span></h2></Anim>
            <Anim delay={0.1}><p className="text-base text-bone/70 leading-relaxed max-w-2xl mx-auto mb-10">Whether you&apos;re just starting to try, managing PCOS or male factor challenges, or preparing for IVF — Fitly&apos;s couple program gives both partners the best nutritional foundation for your shared goal.</p></Anim>
            <Anim delay={0.2}>
              <div className="mb-10">
                <p className="font-serif text-2xl text-bone mb-1"><span className="text-accent">Fitly</span> India</p>
                <p className="text-sm text-bone/60 tracking-wider uppercase mb-1">Couple Fertility Nutrition</p>
                <p className="text-xs text-accent tracking-wider">Her Health. His Health. Your Baby.</p>
              </div>
            </Anim>
            <Anim delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/consultation" data-testid="cf-final-cta-book" className="inline-flex items-center justify-center gap-2 bg-accent text-emerald_deep px-10 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">Book Your Couple Consultation <ArrowRight size={16} /></a>
                <a href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent("Hi! We'd like to learn more about Fitly India's couple fertility nutrition program.")}`} target="_blank" rel="noopener noreferrer" data-testid="cf-final-cta-whatsapp" className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone px-8 py-3.5 text-sm tracking-wide hover:bg-bone/10 transition-all rounded-full"><MessageCircle size={16} /> Message on WhatsApp</a>
              </div>
            </Anim>
          </div>
        </section>

        <aside className="py-10 bg-bone border-t border-border_custom" data-testid="cf-disclaimer">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-xs text-text_muted/70 leading-relaxed text-center">
              <strong className="text-text_muted">Medical Disclaimer:</strong> Information on this page is for educational purposes and does not constitute diagnosis or medical treatment. Fertility conditions require evaluation by qualified healthcare professionals. Nutrition and lifestyle interventions cannot guarantee conception or treatment success and should not replace medical care.
            </p>
          </div>
        </aside>
      </main>

      <LandingFooter />
    </div>
  );
}
